/**
 * Network Grouped Balances Component
 * 
 * PRD-31: Displays wallet balances grouped by blockchain network.
 * Each network shows:
 * - Network name with proper icon (NetworkIcon component)
 * - Total USD value for that network
 * - Full deposit address (LA) with "Tracked" badge
 * - Individual token balances
 * 
 * Design Canon Compliance:
 * - Uses semanticTokens for all styling
 * - Uses networkBrandColors from theme.ts (SSOT)
 * - Uses fmtCurrency/fmtAmount from formatters.ts (no hardcoded .toLocaleString)
 * - 240ms motion timing
 * - Liquid Glass card styling
 * - Touch targets ≥44px
 * - Focus rings on interactive elements
 */

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardInteractiveItem } from '@/components/ui/card';
import { CurrencyIcon } from '@/components/ui/CurrencyIcon';
import { Numbers, Text } from '@/components/ui/typography';
import { fmtAmount, fmtCurrency } from '@/lib/formatters';
import { cn } from '@/lib/utils';
import { getNetworkBrandColor, semanticTokens } from '@packages/config/theme';
import { SFIcon } from '@/components/ui/sf-icon';
import { sfIcons } from '@/lib/sf-icons';
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { BridgeWallet } from './hooks/useWallet';

// Network display names (brand colors come from theme.ts)
const NETWORK_NAMES: Record<string, string> = {
  ethereum: 'Ethereum',
  base: 'Base',
  solana: 'Solana',
  polygon: 'Polygon',
  arbitrum: 'Arbitrum',
  optimism: 'Optimism',
  avalanche: 'Avalanche',
  stellar: 'Stellar',
  tron: 'Tron',
};

// Map Bridge network names to cryptocurrency-icons symbols
const NETWORK_SYMBOL_MAP: Record<string, string> = {
  'ethereum': 'ETH',
  'bitcoin': 'BTC',
  'polygon': 'MATIC',
  'solana': 'SOL',
  'avalanche': 'AVAX',
  'optimism': 'OP',
  'arbitrum': 'ARB',
  'tron': 'TRX',
  'stellar': 'XLM',
  'base': 'BASE',
};

// Stablecoin list for duplicate value detection (amount ≈ usdValue)
const STABLECOINS = ['USDC', 'USDT', 'USDB', 'DAI', 'USDP', 'PYUSD', 'EURC'];

interface NetworkGroup {
  chain: string;
  networkName: string;
  totalUsdValue: number;
  depositAddress: string | null;
  walletAddress: string;
  walletId: string;
  bridgeWalletId: string;
  isTracked: boolean;
  balances: {
    currency: string;
    amount: string;
    usdValue: number;
    isStablecoin: boolean; // For duplicate value detection
  }[];
}

interface NetworkGroupedBalancesProps {
  wallets: BridgeWallet[];
  onAddFunds?: () => void;
  showZeroBalances?: boolean;
}

export function NetworkGroupedBalances({
  wallets,
  onAddFunds,
  showZeroBalances: initialShowZero = false
}: NetworkGroupedBalancesProps) {
  const navigate = useNavigate();
  const [showZeroBalances, setShowZeroBalances] = useState(initialShowZero);

  // Group wallets by network
  const networkGroups = useMemo(() => {
    const groups: NetworkGroup[] = [];

    for (const wallet of wallets) {
      const chain = wallet.chain?.toLowerCase() || 'unknown';
      const networkName = NETWORK_NAMES[chain] || chain.charAt(0).toUpperCase() + chain.slice(1);

      // Calculate balances for this wallet
      const balances = wallet.balances.map(b => {
        const amount = parseFloat(b.balance || '0');
        const currency = b.currency.toUpperCase();
        const isStablecoin = STABLECOINS.includes(currency);
        let usdValue = 0;

        if (b.usd_value) {
          usdValue = Math.floor(parseFloat(b.usd_value) * 100) / 100;
        } else if (amount > 0) {
          const truncatedAmount = Math.floor(amount * 100) / 100;

          if (['USDC', 'USDT', 'USDB', 'DAI', 'USDP', 'PYUSD'].includes(currency)) {
            usdValue = truncatedAmount;
          } else if (currency === 'EURC') {
            usdValue = Math.floor(truncatedAmount * 0.93 * 100) / 100;
          } else {
            usdValue = truncatedAmount;
          }
        }

        return {
          currency,
          amount: b.balance,
          usdValue,
          isStablecoin, // Track stablecoins for duplicate value detection
        };
      });

      const totalUsdValue = balances.reduce((sum, b) => sum + b.usdValue, 0);

      groups.push({
        chain,
        networkName,
        totalUsdValue,
        depositAddress: wallet.deposit_address || null,
        walletAddress: wallet.address,
        walletId: wallet.id,
        bridgeWalletId: wallet.bridge_wallet_id,
        isTracked: !!wallet.deposit_address,
        balances
      });
    }

    // Sort by total USD value (descending), then by network name
    return groups.sort((a, b) => {
      if (b.totalUsdValue !== a.totalUsdValue) {
        return b.totalUsdValue - a.totalUsdValue;
      }
      return a.networkName.localeCompare(b.networkName);
    });
  }, [wallets]);

  // Filter based on showZeroBalances
  const visibleGroups = useMemo(() => {
    if (showZeroBalances) return networkGroups;
    return networkGroups.filter(g => g.totalUsdValue > 0);
  }, [networkGroups, showZeroBalances]);

  const hiddenCount = networkGroups.length - visibleGroups.length;

  const handleNetworkClick = (walletId: string) => {
    navigate(`/dashboard/wallet/${walletId}`);
  };

  // Check if USD value should be shown for a balance
  // For stablecoins (USDC, USDT, etc.), don't show USD value if it's ~equal to amount
  // This prevents displaying "1.96$1.96" for stablecoins
  const shouldShowUsdValue = (balance: { usdValue: number; amount: string; isStablecoin: boolean }): boolean => {
    if (balance.usdValue <= 0) return false;
    if (!balance.isStablecoin) return true; // Always show for non-stablecoins

    // For stablecoins, hide if amount ≈ usdValue (within 1% difference)
    const amountNum = parseFloat(balance.amount);
    if (isNaN(amountNum)) return false;
    const difference = Math.abs(amountNum - balance.usdValue);
    const threshold = Math.max(amountNum, balance.usdValue) * 0.01; // 1% tolerance
    return difference > threshold;
  };

  if (visibleGroups.length === 0 && !showZeroBalances) {
    return (
      <div className={cn("text-center py-8", semanticTokens.stack.md)}>
        <Text variant="md" color="muted">
          No balances to show
        </Text>
        {hiddenCount > 0 && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setShowZeroBalances(true)}
            className={cn("mt-2 min-h-[44px]", semanticTokens.motion.standard)}
          >
            <SFIcon icon={sfIcons.eye} className="w-4 h-4 mr-2" aria-hidden="true" />
            Show {hiddenCount} empty {hiddenCount === 1 ? 'wallet' : 'wallets'}
          </Button>
        )}
      </div>
    );
  }

  return (
    <div className={cn(semanticTokens.stack.lg)}>
      {/* Toggle for zero balances */}
      {hiddenCount > 0 && (
        <div className="flex justify-end">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setShowZeroBalances(!showZeroBalances)}
            className="min-h-[44px] text-xs"
          >
            {showZeroBalances ? (
              <>
                <SFIcon icon={sfIcons.eyeOff} className="w-4 h-4 mr-2" aria-hidden="true" />
                Hide empty wallets ({hiddenCount})
              </>
            ) : (
              <>
                <SFIcon icon={sfIcons.eye} className="w-4 h-4 mr-2" aria-hidden="true" />
                Show all wallets (+{hiddenCount})
              </>
            )}
          </Button>
        </div>
      )}

      {/* Network Groups */}
      <div className={cn("grid gap-4 md:gap-6")}>
        {visibleGroups.map((group) => (
          <Card
            key={group.chain}
            className={cn(
              semanticTokens.bg.surface,
              semanticTokens.border.default,
              semanticTokens.radius.xl,
              semanticTokens.shadow.sm,
              semanticTokens.motion.standard,
              "overflow-hidden"
            )}
          >
            <CardContent className="p-0">
              {/* Network Header - Clickable */}
              <CardInteractiveItem
                onClick={() => handleNetworkClick(group.walletId)}
                aria-label={`View ${group.networkName} wallet details`}
              >
                <div className="flex items-center justify-between p-4 md:p-6">
                  <div className="flex items-center gap-3 md:gap-4">
                    {/* Network Icon - Using CurrencyIcon directly, filling the container */}
                    <div className={cn(
                      "w-12 h-12 md:w-14 md:h-14 rounded-full flex items-center justify-center overflow-hidden p-1",
                      getNetworkBrandColor(group.chain).bg,
                      getNetworkBrandColor(group.chain).text
                    )}>
                      <CurrencyIcon
                        symbol={NETWORK_SYMBOL_MAP[group.chain.toLowerCase()] || group.chain.toUpperCase()}
                        size={56}
                        colored={true}
                        className="w-[40px] h-[40px] md:w-[48px] md:h-[48px] [&>img]:!w-full [&>img]:!h-full [&>img]:!min-w-0 [&>img]:!min-h-0 [&>img]:object-contain"
                      />
                    </div>

                    {/* Network Info */}
                    <div>
                      <Text variant="lg">
                        {group.networkName}
                      </Text>
                      <Text variant="caption" color="muted">
                        {group.balances.length} {group.balances.length === 1 ? 'token' : 'tokens'}
                      </Text>
                    </div>
                  </div>

                  {/* Total Value & Arrow */}
                  <div className="flex items-center gap-3">
                    <div className="text-right">
                      <Numbers variant="md">
                        {fmtCurrency(group.totalUsdValue)}
                      </Numbers>
                    </div>
                    <SFIcon icon={sfIcons.chevronRight} className={cn("w-5 h-5", semanticTokens.fg.muted)} aria-hidden="true" />
                  </div>
                </div>
              </CardInteractiveItem>

              {/* Token Balances */}
              <div className={cn("border-t", semanticTokens.border.subtle)}>
                {group.balances.map((balance, idx) => {
                  const isZeroBalance = parseFloat(balance.amount) === 0;

                  return (
                    <div
                      key={balance.currency}
                      className={cn(
                        "flex items-center justify-between px-4 md:px-6 py-3",
                        idx !== group.balances.length - 1 && "border-b",
                        semanticTokens.border.subtle
                      )}
                    >
                      <div className="flex items-center gap-3">
                        <div className={cn(
                          "w-8 h-8 rounded-full flex items-center justify-center overflow-hidden",
                          semanticTokens.bg.muted
                        )}>
                          <CurrencyIcon symbol={balance.currency} size={28} colored={true} />
                        </div>
                        <div>
                          <Text variant="md" as="span">
                            {balance.currency}
                          </Text>
                          {isZeroBalance && (
                            <Text variant="caption" color="muted" className="block">
                              Ready to receive
                            </Text>
                          )}
                        </div>
                      </div>
                      <div className="text-right flex items-center gap-3">
                        {isZeroBalance ? (
                          // Show "Add" button for zero balances
                          onAddFunds && (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={(e) => {
                                e.stopPropagation();
                                onAddFunds();
                              }}
                              className={cn("min-h-[36px] text-xs", semanticTokens.motion.standard)}
                            >
                              <SFIcon icon={sfIcons.plus} className="w-3 h-3 mr-1" aria-hidden="true" />
                              Add
                            </Button>
                          )
                        ) : (
                          <div>
                            <Numbers variant="sm">
                              {fmtAmount(balance.amount)}
                            </Numbers>
                            {/* Only show USD value if different from amount (prevents "1.96$1.96" for stablecoins) */}
                            {shouldShowUsdValue(balance) && (
                              <Text variant="caption" color="muted">
                                {fmtCurrency(balance.usdValue)}
                              </Text>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
}
